import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Send, Gift, Zap, Star, Gem } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const typeConfig = {
  post: { icon: Gift, label: "Post", color: "#24A1DE" },
  boost: { icon: Zap, label: "Boost", color: "#FF9F0A" },
  premium: { icon: Star, label: "Premium", color: "#AF52DE" },
  fragment: { icon: Gem, label: "Fragment", color: "#34C759" },
};

export default function RewardsPage() {
  const [rewards, setRewards] = useState([]);
  const [groups, setGroups] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", reward_type: "post", value: "", target_group_id: "all" });
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [rRes, gRes] = await Promise.all([api.get("/rewards"), api.get("/groups")]);
      setRewards(rRes.data.rewards);
      setGroups(gRes.data.groups);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const addReward = async (e) => {
    e.preventDefault();
    try {
      await api.post("/rewards", { ...form, target_group_id: form.target_group_id === "all" ? "" : form.target_group_id });
      toast.success("Odul olusturuldu");
      setShowAdd(false);
      setForm({ name: "", description: "", reward_type: "post", value: "", target_group_id: "all" });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Hata"); }
  };

  const deleteReward = async (id) => {
    try {
      await api.delete(`/rewards/${id}`);
      toast.success("Odul silindi");
      loadData();
    } catch (err) { toast.error("Silme basarisiz"); }
  };

  const distributeReward = async (id) => {
    try {
      const { data } = await api.post(`/rewards/${id}/distribute`);
      toast.success(data.message);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Dagitim basarisiz"); }
  };

  return (
    <div data-testid="rewards-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Oduller</h1>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button data-testid="add-reward-btn" className="bg-[#24A1DE] hover:brightness-110 text-white">
              <Plus className="w-4 h-4 mr-2" />Odul Olustur
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-[#27272A]">
            <DialogHeader>
              <DialogTitle className="text-white font-heading">Yeni Odul</DialogTitle>
            </DialogHeader>
            <form onSubmit={addReward} className="space-y-4">
              <div className="space-y-1">
                <Label className="text-[#A1A1AA] text-sm">Odul Adi</Label>
                <Input data-testid="reward-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="bg-[#2C2C2E] border-transparent text-white" required />
              </div>
              <div className="space-y-1">
                <Label className="text-[#A1A1AA] text-sm">Aciklama</Label>
                <Input data-testid="reward-description" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="bg-[#2C2C2E] border-transparent text-white" />
              </div>
              <div className="space-y-1">
                <Label className="text-[#A1A1AA] text-sm">Tur</Label>
                <Select value={form.reward_type} onValueChange={(v) => setForm({...form, reward_type: v})}>
                  <SelectTrigger data-testid="reward-type-select" className="bg-[#2C2C2E] border-transparent text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C1C1E] border-[#27272A]">
                    <SelectItem value="post">Post</SelectItem>
                    <SelectItem value="boost">Boost</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="fragment">Fragment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[#A1A1AA] text-sm">Deger</Label>
                <Input data-testid="reward-value" value={form.value} onChange={(e) => setForm({...form, value: e.target.value})} className="bg-[#2C2C2E] border-transparent text-white" placeholder="Odul degeri" />
              </div>
              <div className="space-y-1">
                <Label className="text-[#A1A1AA] text-sm">Hedef Grup</Label>
                <Select value={form.target_group_id} onValueChange={(v) => setForm({...form, target_group_id: v})}>
                  <SelectTrigger data-testid="reward-target-group" className="bg-[#2C2C2E] border-transparent text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C1C1E] border-[#27272A]">
                    <SelectItem value="all">Tum kullanicilar</SelectItem>
                    {groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="submit-reward" type="submit" className="w-full bg-[#24A1DE] hover:brightness-110">Olustur</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rewards.map(reward => {
          const cfg = typeConfig[reward.reward_type] || typeConfig.post;
          const Icon = cfg.icon;
          return (
            <Card key={reward.id} className="bg-[#121212] border-[#27272A] hover:border-[#3f3f46] transition-colors duration-150" data-testid={`reward-card-${reward.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className="w-5 h-5" style={{ color: cfg.color }} />
                    <CardTitle className="text-white text-base font-heading">{reward.name}</CardTitle>
                  </div>
                  <Badge style={{ borderColor: cfg.color, color: cfg.color }} variant="outline" className="text-xs">{cfg.label}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-[#A1A1AA] text-sm mb-2">{reward.description || "Aciklama yok"}</p>
                {reward.value && <p className="text-white text-sm font-mono mb-2">Deger: {reward.value}</p>}
                <p className="text-[#A1A1AA] text-xs mb-3">Dagitilan: {reward.distributed_count || 0}</p>
                <div className="flex gap-2">
                  <Button data-testid={`distribute-reward-${reward.id}`} size="sm" onClick={() => distributeReward(reward.id)} className="bg-[#34C759] hover:brightness-110 text-white flex-1 h-8">
                    <Send className="w-3 h-3 mr-1" />Dagit
                  </Button>
                  <Button data-testid={`delete-reward-${reward.id}`} size="sm" variant="ghost" onClick={() => deleteReward(reward.id)} className="text-[#FF3B30] hover:bg-[#2C2C2E] h-8 w-8 p-0">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {rewards.length === 0 && !loading && (
          <div className="col-span-full text-center text-[#A1A1AA] py-16 text-sm">Henuz odul olusturulmamis</div>
        )}
      </div>
    </div>
  );
}
